import React from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { Checkbox } from 'react-bootstrap';
import { cx, css } from 'emotion';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Flexbox from 'flexbox-react';
import {
    DiagramEngine,
    DiagramModel,
    DefaultNodeModel,
    DefaultNodeWidget,
    DefaultNodeFactory,
    DefaultLinkModel,
    DefaultPortModel,
    DefaultLinkFactory,
    DefaultLinkWidget,
    DiagramWidget,
    Toolkit
} from 'storm-react-diagrams';
import dagre from 'dagre';
import ProcessService from 'services/ProcessService';
import DodService from 'services/DodService';
import NavigationUtils from 'utils/NavigationUtils';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import { colorBlue, colorIconBlue, colorLightIconBlue, colorVeryLightBlue, colorPurple, colorLightPurple,
    colorTextGrey, colorBorderGrey, colorGrey } from 'styles/base';

const noRightBorderStyle = css`border-right:none !important;`;
const checkboxStyle = css`margin-top: 5px;margin-bottom: 5px;`;

class DependencyTree extends React.Component {
    static propTypes = {
        dependencies: PropTypes.object,
        id: PropTypes.string,
        processes: PropTypes.object,
        dods: PropTypes.object,
        prodDods: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {
            firstLevelOnly: false,
            productionOnly: true,
            diagramView: true
        };

        this.data = null;
    }

    componentWillMount() {
        // Get the process id from the route
        ProcessService.fetchDependencyTreeIfNeeded(this.props.id);
        ProcessService.fetchProcessesIfNeeded();
        this.fetchProcess(this.props.id);
        DodService.fetchDodsIfNeeded();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.match.params.id !== this.props.id) {
            ProcessService.fetchDependencyTreeIfNeeded(nextProps.match.params.id);
            this.fetchProcess(nextProps.match.params.id)
        }
    }

    fetchProcess(id) {
        if (id.indexOf('.') === -1) {
            // dods got that . in their name, not allowed in process
            ProcessService.fetchOriginalProcessIfNeeded(id);
        }
    }

    componentDidMount() {
        if (this.refs[`${this.props.id}-diagram`]) {
            this.setDiagramPosition();
        }
    }

    componentDidUpdate() {
        if (this.refs[`${this.props.id}-diagram`]) {
            this.setDiagramPosition();
        }
    }

    formatData = () => {
        const productionOnly = this.state.productionOnly;
        const dependencies = this.props.dependencies;
        let data = { nodes: {}, links: [] };

        let centerNodeId = dependencies.uid;
        let type = this.getType(centerNodeId);

        data.nodes[centerNodeId] = {
            config: {
                label: centerNodeId,
                type,
                prod: this.isProd(dependencies, type)
            },
            isSource: true
        };

        const getChildrenFunction = (parent, children, grandparent, parentIsRaw) => {
            children.forEach(child => {
                let includeNode = true;
                let direction = null;
                let getChildren = true;
                let childIsRaw = false;

                //determine node type
                type = this.getType(child.uid);
                const prod = this.isProd(child, type);

                if (type === 'process' && !child.uid.match(/(:VAP|:Ingest)/)) {
                    //filter out all processes that aren't ingest or vap
                    includeNode = false;
                } else if (productionOnly && !prod) {
                    //filter out development processes based on state
                    includeNode = false;
                }

                //separate circular dependency into "raw" (or intermediate datastream) input and datastream output
                if (grandparent) {
                    if (grandparent.uid === child.uid && type === 'dod') {
                        const parentNode = data.nodes[parent.uid];

                        //if the circular dependency is upstream the child is the "raw" input
                        //otherwise the grandparent in the circle is the "raw" input
                        if (parentNode.direction === 'upstream') {
                            childIsRaw = true;
                        } else {
                            const newId = `${grandparent.uid}-raw`;
                            if (grandparent.uid === centerNodeId) {
                                centerNodeId = newId;
                            }
                            //update the old node to reflect our new found info that it's actually "raw" input
                            data.nodes[newId] = data.nodes[grandparent.uid];
                            data.nodes[newId].config.type = 'raw';

                            if (parent.uid === centerNodeId) {
                                data.nodes[newId].direction = 'upstream';
                            }

                            delete data.nodes[grandparent.uid];

                            //update all grandparent's links as well
                            data.links = data.links.map(link => {
                                let sourceId = link.sourceId;
                                let targetId = link.targetId;
                                if (sourceId === grandparent.uid) {
                                    sourceId = newId;
                                } else if (targetId === grandparent.uid) {
                                    targetId = newId;
                                }
                                return { sourceId, targetId };
                            });
                        }
                    }
                }

                if (includeNode) {
                    //create the node if it doesn't already exist
                    let childUid = child.uid;
                    if (childIsRaw) {
                        childUid = `${child.uid}-raw`;
                        type = 'raw';
                    }

                    let parentUid = parent.uid;
                    if (parentIsRaw) {
                        parentUid = `${parent.uid}-raw`;
                    }

                    if (!data.nodes[childUid]) {
                        data.nodes[childUid] = {
                            config: {
                                label: child.uid,
                                type,
                                prod,
                                required: this.isRequired(child)
                            }
                        };
                    }

                    //determine if node is upstream or downstream
                    if (parent.position === 'center') {
                        //used when switching grandparent to "raw" because it will also need to be updated to upstream if it's a first level child node
                        data.nodes[childUid].isFirstLevel = true;

                        if (child.position === 'left') {
                            direction = 'upstream';
                        } else if (child.position === 'right') {
                            direction = 'downstream';
                        }
                    } else {
                        if (child.position === 'left' && data.nodes[parentUid].direction === 'upstream') {
                            direction = 'upstream';
                        } else if (child.position === 'right' && data.nodes[parentUid].direction === 'downstream') {
                            direction = 'downstream';
                        } else if (childIsRaw && data.nodes[parentUid].direction === 'upstream') {
                            direction = 'upstream';
                        } else if (childIsRaw) {
                            direction = null;
                        }
                    }

                    data.nodes[childUid].direction = direction;

                    //create link
                    let sourceId = null;
                    let targetId = null;

                    if (child.position === 'right') {
                        sourceId = childUid;
                        targetId = parent.uid;
                    } else if (child.position === 'left') {
                        sourceId = parent.uid;
                        targetId = childUid;
                    }

                    //check for duplicate
                    let linkExists = false;
                    for (let i = 0; i < data.links.length; i++) {
                        const link = data.links[i];
                        if (link.sourceId === sourceId && link.targetId === targetId) {
                            linkExists = true;
                            break;
                        }
                    }

                    if (!linkExists) {
                        data.links.push({ sourceId, targetId });
                    }

                } else {
                    //don't get children of filtered out nodes
                    getChildren = false;
                }

                if (child.children.length > 0 && getChildren) {
                    data = getChildrenFunction(child, child.children, parent, childIsRaw);
                }
            });

            return data;
        };

        if (dependencies.children.length > 0) {
            data = getChildrenFunction(dependencies, dependencies.children, null, false);
        }

        if (productionOnly && !this.state.firstLevelOnly && data.links.length === 0 && !this.data) {
            //check that this.data is null because we only want to do this on first render
            this.setState({ firstLevelOnly: true, productionOnly: false });
        }

        if (this.state.firstLevelOnly) {
            //need to just do this after the fact because it's too complicated getting first level depth
            //with "raw" inputs should be first level, but are 3 levels deep in object because of the structure
            data = this.getFirstLevelData(centerNodeId, data);
        }

        this.data = data;
        return data;
    };

    getFirstLevelData = (sourceUid, data) => {
        const filteredData = { nodes: {}, links: [] };
        filteredData.nodes[sourceUid] = data.nodes[sourceUid];
        data.links.forEach(link => {
            if (link.targetId === sourceUid || link.sourceId === sourceUid) {
                filteredData.links.push(link);

                if (!filteredData.nodes[link.targetId]) {
                    filteredData.nodes[link.targetId] = data.nodes[link.targetId];
                }

                if (!filteredData.nodes[link.sourceId]) {
                    filteredData.nodes[link.sourceId] = data.nodes[link.sourceId];
                }
            }
        });
        return filteredData;
    };

    isProd = (item, type) => {
        const processes = this.props.processes;
        let prod = false;
        let name = item.name;

        if (type === 'dod') {
            name = item.uid;
            if (this.props.prodDods[name]) {
                prod = true;
            }
        } else {
            if (processes[name]) {
                if (processes[name].prod) {
                    prod = true;
                }
            }
        }

        return prod;
    };

    isRequired = (item) => {
        let required = false;

        if (this.props.process && this.props.process.variable_retrieval) {
            Object.values(this.props.process.variable_retrieval.input_datasets).forEach(dataset => {
                dataset.rules.forEach(rule => {
                    if (rule.datastream_name === item.uid) {
                        Object.keys(rule.variable_mappings).forEach(retrievedVariableName => {
                            const variableRequired = this.props.process.variable_retrieval.retrieved_variables[retrievedVariableName].variable_required;
                            if (variableRequired === 1) {
                                required = true;
                            }
                        });
                    }
                });
            });
        }

        return required;
    };

    getType = (id) => {
        const segs = id.split(':');
        let type = 'dod';
        if (this.props.processes[segs[0]]) {
            type = 'process';
        }
        return type;
    };

    getColor = (type, prod, isListItem) => {
        let color = null;

        if (type === 'process') {
            if (prod || isListItem) {
                color = colorPurple;
            } else {
                color = colorLightPurple;
            }
        } else {
            if (prod || isListItem) {
                color = colorIconBlue;
            } else {
                color = colorLightIconBlue;
            }
        }
        return color;
    };

    getIcon = (nodeConfig) => {
        let icon = null;
        const color = nodeConfig.prod ? 'green' : 'yellow';

        if (nodeConfig.type === 'process') {
            //production process icon
            icon = `fa-cogs ${color}`;
        } else {
            //show folder? for .0* dods
            const match = nodeConfig.label.match(/^.*\.0\d$/);
            if (match) {
                icon = `fa-folder-open ${color}`;
            } else {
                icon = `fa-share-alt-square ${color}`;
            }
        }

        return icon;
    };

    getLabel = (labelField, required, forNode) => {
        let label = <span>{labelField}</span>;
        if (required) {
            label = <span title="Bold datastream has required variables for selected process."><b>{labelField}</b></span>
        }
        const segs = labelField.split(':');

        if (segs.length > 1) {
            let processTypeStyle = css`color: #c1c1c1; padding-left:4px;`;
            if (forNode) {
                //want label in graph node to have slightly different styles
                processTypeStyle = css`color: ${colorGrey}; padding-left:2px;`;
            }

            label = <span> {segs[0]} <span className={cx(processTypeStyle)}>{segs[1]}</span></span>;
        }
        return label;
    };

    createNode = (id, config) => {
        const color = this.getColor(config.type, config.prod, false);
        const icon = this.getIcon(config);
        return new CustomNodeModel(id, color, this.getLabel(config.label, config.required, true), icon);
    };

    connectNodes = (nodeSource, nodeTarget) => {
        //just to get id-like structure
        let portOut = nodeSource.getPort(`${nodeSource.name}-out`);
        let portTo = nodeTarget.getPort(`${nodeTarget.name}-in`);

        if (!portOut) {
            portOut = nodeSource.addPort(new CustomPortModel(true, `${nodeSource.name}-out`, ' '));
        }

        if (!portTo) {
            portTo = nodeTarget.addPort(new CustomPortModel(false, `${nodeTarget.name}-in`, ' '));
        }

        const link = portOut.link(portTo);
        link.name = `${nodeSource.name}-${nodeTarget.name}`;
        return link;
    };

    condenseLinks = (links) => {
        // condense links if there is two way flow between nodes
        const adjustedLinks = [];
        links.forEach(linkA => {
            let pushLink = linkA;
            for (let i = 0; i < adjustedLinks.length; i++) {
                const linkB = adjustedLinks[i];

                //link flows both directions
                if (linkA.sourcePort.parent.id === linkB.targetPort.parent.id && linkA.targetPort.parent.id === linkB.sourcePort.parent.id) {
                    if (linkA.sourcePort.parent.name.indexOf('-raw') < 0) {
                        adjustedLinks.splice(i, 1);
                    } else if (linkA.sourcePort.parent.name.indexOf(':') > 0 && linkA.targetPort.parent.name.indexOf('-raw') < 0) {
                        adjustedLinks.splice(i, 1);
                    } else {
                        pushLink = false;
                    }
                    break;
                }
            }

            if (pushLink) {
                adjustedLinks.push(pushLink);
            }
        });

        return adjustedLinks;
    };

    getNodeLayout = (nodes, links) => {
        const model = new DiagramModel();
        const graph = new dagre.graphlib.Graph();

        graph.setGraph({
            marginx: 0,
            marginy: 0,
            nodesep: 20,
            rankdir: 'LR',
            ranker: 'tight-tree',
            ranksep: 30,
        });

        graph.setDefaultEdgeLabel(() => ({}));

        Object.keys(nodes).forEach(nodeId => {
            const node = nodes[nodeId];
            const id = node.name;
            graph.setNode(id, { name: id, width: 170, height: 50 });
        });

        links.forEach(link => {
            graph.setEdge(link.targetPort.parent.name, link.sourcePort.parent.name);
        });

        dagre.layout(graph);

        Object.keys(nodes).forEach(nodeId => {
            const dagreNode = graph._nodes[nodeId];
            const node = nodes[nodeId];
            node.setPosition(dagreNode.x, dagreNode.y);

            model.addNode(node);
        });

        links.forEach(link => {
            model.addLink(link);
        });

        return model;
    };

    getDependencyLists = (data) => {
        //breakout data into upstream/downstream processes/datastreams lists
        const lists = {
            upstream: {
                processes: {},
                dods: {}
            },
            downstream: {
                processes: {},
                dods: {}
            }
        };

        Object.keys(data.nodes).forEach((nodeId) => {
            const node = data.nodes[nodeId];
            const isProcess = node.config.type === 'process';
            let type = node.config.type;
            const prod = node.config.prod;

            const icon = <i className={`fa ${this.getIcon(node.config)}`} />;
            if (isProcess) {
                //want the development icon but main process color
                type = 'process';
            }
            const listItemStyles = css`
                color: ${this.getColor(type, prod, true)};
                white-space: nowrap;
                &:hover {
                    cursor: pointer;
                }
            `;
            const listItem = (
                <tr
                    key={nodeId}
                    className={`${type} ${cx(listItemStyles)}`}
                    onDoubleClick={() => { this.navigateToDependencies(node.config.label) }}
                >
                    <td>{icon} {this.getLabel(node.config.label, node.config.required)}</td>
                </tr>
            );

            if (node.direction === 'upstream') {
                if (isProcess) {
                    lists.upstream.processes[nodeId] = listItem;
                } else {
                    lists.upstream.dods[nodeId] = listItem;
                }
            } else if (node.direction === 'downstream') {
                if (isProcess) {
                    lists.downstream.processes[nodeId] = listItem;
                } else {
                    lists.downstream.dods[nodeId] = listItem;
                }
            }
        });

        const sortFunc = (listObj) => {
            let list = [];
            const sortedKeys = Object.keys(listObj).sort();

            sortedKeys.forEach(key => {
                list.push(listObj[key]);
            });

            return list;
        };

        lists.upstream.processes = sortFunc(lists.upstream.processes);
        lists.upstream.dods = sortFunc(lists.upstream.dods);
        lists.downstream.processes = sortFunc(lists.downstream.processes);
        lists.downstream.dods = sortFunc(lists.downstream.dods);

        return lists;
    };

    onViewToggle = () => {
        //toggle diagram view and text view
        let diagramView = true;
        if (this.state.diagramView) {
            diagramView = false;
        }

        this.setState({ diagramView });
    };

    onProductionToggle = () => {
        let productionOnly = true;
        if (this.state.productionOnly) {
            productionOnly = false;
        }

        this.setState({ productionOnly });
    };

    onFirstLevelToggle = () => {
        let firstLevelOnly = true;
        if (this.state.firstLevelOnly) {
            firstLevelOnly = false;
        }

        this.setState({ firstLevelOnly });
    };

    navigateToDependencies = (label) => {
        const segs = label.split(':');
        const id = segs[0];
        NavigationUtils.gotoDependencyTree(id, false);
    };

    setDiagramPosition = () => {
        const engine = this.refs[`${this.props.id}-diagram`].props.diagramEngine;
        const model = engine.diagramModel;

        // center source node if it would normally appear off screen on initial load
        let sourceNodePos = { x: 0, y: 0 };
        for (let i = 0; i < Object.keys(model.nodes).length; i++) {
            const node = model.nodes[Object.keys(model.nodes)[i]];
            if (node.isSource) {
                sourceNodePos = { x: node.x, y: node.y };
                break;
            }
        }

        const container = ReactDOM.findDOMNode(this.refs[`${this.props.id}-diagram-container`]);
        if (container) {
            const containerWidth = ReactDOM.findDOMNode(this.refs[`${this.props.id}-diagram-container`]).clientWidth;
            const containerHeight = ReactDOM.findDOMNode(this.refs[`${this.props.id}-diagram-container`]).clientHeight;
            let changeXOffset = false;

            //minus for some padding if it's really close to the right edge
            if (sourceNodePos.x > containerWidth - 150) {
                sourceNodePos.x = sourceNodePos.x - containerWidth / 2;
                changeXOffset = true;
            }

            let changeYOffset = false;
            if (sourceNodePos.y > containerHeight - 75) {
                sourceNodePos.y = sourceNodePos.y - containerHeight / 2;
                changeYOffset = true;
            } else if (sourceNodePos.y < 75) {
                //give it some top padding if it's right at the top
                //looks fine for source to be close the left side though so that's why I'm not doing this same thing on the x axis
                sourceNodePos.y = -75;
                changeYOffset = true;
            }

            if (changeXOffset) {
                model.setOffsetX(-sourceNodePos.x);
            }
            if (changeYOffset) {
                model.setOffsetY(-sourceNodePos.y);
            }
        }

        engine.setDiagramModel(model);
        engine.repaintCanvas();
    };

    renderTree = (data) => {
        const engine = new DiagramEngine();
        engine.installDefaultFactories();
        engine.registerLinkFactory(new ArrowLinkFactory());
        engine.registerNodeFactory(new CustomNodeFactory());

        const nodes = {};
        Object.keys(data.nodes).forEach(nodeId => {
            const config = data.nodes[nodeId].config;
            nodes[nodeId] = this.createNode(nodeId, config);

            if (data.nodes[nodeId].isSource) {
                nodes[nodeId].isSource = true;
            }
        });

        let links = [];
        data.links.forEach(link => {
            const nodeSource = nodes[link.sourceId];
            const nodeTarget = nodes[link.targetId];
            links.push(this.connectNodes(nodeSource, nodeTarget));
        });
        links = this.condenseLinks(links);

        const model = this.getNodeLayout(nodes, links, data);
        engine.setDiagramModel(model);

        return <DiagramWidget className="srd-demo-canvas" diagramEngine={engine} ref={`${this.props.id}-diagram`}/>;
    };

    renderList = (label, items, totalRows, rightBorder) => {
        const emptyStyles = css`
            color: #c1c1c1;
            font-style: italic;
            text-align: center;
        `;

        const borderStyle = !rightBorder ? noRightBorderStyle : null;
        const emptyContent = <tr><td className={cx(emptyStyles)}>None</td></tr>;

        const fillSpace = [];
        let span = 0;
        if (items.length === 0 && totalRows > 1) {
            span = totalRows;
        } else if (items.length < totalRows && totalRows > 1) {
            span = totalRows - items.length + 1;
        }

        if (span > 0) {
            fillSpace.push(
                <tr key={0}>
                    <td rowSpan={span}/>
                </tr>
            );
            for (let i = 1; i < span; i++) {
                fillSpace.push(
                    <tr key={i}>
                        <td style={{ width: 0, padding: 0 }}/>
                    </tr>
                );
            }
        }

        return (
            <Flexbox flexDirection={'column'} flexGrow={1} className={'list-column'}>
                <h4 className={cx(borderStyle)}>{label}</h4>
                <table className={cx(borderStyle)}>
                    <tbody>
                    {items.length > 0 ?
                        items
                        :
                        emptyContent
                    }
                    {fillSpace}
                    </tbody>
                </table>
            </Flexbox>
        );
    };

    renderTextReport = (data) => {
        const list = this.getDependencyLists(data);

        const listStyles = css`
            min-width:300px;
            h3 {
                padding:10px 25px;
                background-color: ${colorBlue};
                color: #fff;
                margin-bottom:0;
                text-align: center;
                border: 1px solid ${colorBorderGrey};
            }
            h4 {
                font-size: 1em !important;
                background-color: ${colorVeryLightBlue};
                margin-top: 0;
                margin-bottom:0;
                padding: 5px 20px;
                text-align: center;
                border: 1px solid ${colorBorderGrey};
                border-top: none;
            }
            table {
                width: 100%;
                border: 1px solid ${colorBorderGrey};
                border-top:none;
                tr:first-child td {
                    padding-top:5px;
                }
            }
            td {
                height:20px;
                padding: 0 10px;
            }
        `;


        const rowCount = Math.max(...[list.upstream.processes.length, list.upstream.dods.length, list.downstream.processes.length, list.downstream.dods.length]);

        return (
            <Flexbox flexDirection={'column'}>
                <Flexbox flexDirection={'row'} style={{ padding: '0 25px' }}>
                    <Flexbox className={cx(listStyles)} flexDirection={'column'}>
                        <h3 className={cx(noRightBorderStyle)}>Upstream</h3>
                        <Flexbox flexDirection={'row'} style={{ height: '100%' }}>
                            {this.renderList('Processes', list.upstream.processes, rowCount, false)}
                            {this.renderList('Datastreams', list.upstream.dods, rowCount, false)}
                        </Flexbox>
                    </Flexbox>
                    <Flexbox className={cx(listStyles)} flexDirection={'column'}>
                        <h3>Downstream</h3>
                        <Flexbox flexDirection={'row'} style={{ height: '100%' }}>
                            {this.renderList('Processes', list.downstream.processes, rowCount, false)}
                            {this.renderList('Datastreams', list.downstream.dods, rowCount, true)}
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            </Flexbox>
        );
    };

    render = () => {
        const styles = { height: 'calc(100vh - 116px)', overflow: 'auto' };

        let content = null;
        let render = false;
        if (this.props.dependencies && Object.keys(this.props.processes).length > 0 && this.props.dods) {
            render = true;
            this.data = this.formatData();

            if (this.state.diagramView) {
                content = this.renderTree(this.data);
            } else {
                content = this.renderTextReport(this.data);
            }
        }

        if (render) {
            return (
                <Flexbox
                    flexGrow={1} flexDirection={'column'} style={styles}
                    ref={`${this.props.id}-diagram-container`}
                >
                    {/* define arrowhead marker before tree graph */}
                    <div style={{ height: '1px', overflow: 'hidden' }}>
                        <svg>
                            <defs>
                                <marker
                                    id="arrow" viewBox="0 0 10 10" refX="15" refY="5" markerWidth="5"
                                    markerHeight="5" orient="auto-start-reverse"
                                >
                                    <path d="M 0 0 L 10 5 L 0 10 z"/>
                                </marker>
                            </defs>
                        </svg>
                    </div>
                    <Flexbox
                        flexDirection={'column'} className="dependency-title"
                        style={{ height: '40px', justifyContent: 'center', flexShrink: '0' }}
                    >
                        <span style={{ margin: '0' }}>
                            <span className="dependency-label">
                                Dependencies for {this.getType(this.props.id) === 'dod' ? 'DOD' : 'process'}:
                            </span>
                            {this.props.id}
                            <ToolbarItem
                                title={this.state.diagramView ? 'Switch to list view' : 'Switch to diagram view'}
                                className={`fa ${this.state.diagramView ? 'fa-list' : 'fa-th-large'} blue`}
                                clickHandler={this.onViewToggle}
                                selectionDependent={false}
                                style={{ display: 'inline-block', marginLeft: '10px' }}
                                enabled
                            />
                        </span>
                    </Flexbox>
                    <div style={{ paddingLeft: '20px' }}>
                        <div>
                            <div className={cx(css`color:${colorTextGrey};`)}>
                                Show only:
                            </div>
                            <Checkbox
                                onChange={this.onFirstLevelToggle} checked={this.state.firstLevelOnly}
                                className={cx(checkboxStyle)}
                            >
                                Dependencies one level down
                            </Checkbox>
                            <Checkbox
                                onChange={this.onProductionToggle} checked={this.state.productionOnly}
                                className={cx(checkboxStyle)}
                            >
                                Dependencies that directly involve production (DMF) processes and dods
                            </Checkbox>
                        </div>
                    </div>
                    {content}
                </Flexbox>
            );
        } else {
            return (<span />);
        }
    };
}

const mapStateToProps = (storeState, ownProps) => {
    const id = ownProps.match.params.id;
    const processState = ProcessService.getMyState(storeState);

    const prodDods = DodService.getMyState(storeState).prodDods;
    const dods = DodService.getMyState(storeState).dodsById;
    const dependencies = processState.dependencyTreeById[id];
    const processes = processState.processesById;

    let process = null;
    if (processes[id]) {
        process = processState.originalCopiesById[id];
    }

    return {
        id,
        dependencies,
        processes,
        dods,
        prodDods,
        process
    };
};

export default connect(mapStateToProps)(DependencyTree);

/**
 * Add custom attribute to indicate whether arrow should flow both directions
 */
class ArrowLinkModel extends DefaultLinkModel {
    constructor(type) {
        super(type);

        this.color = 'rgba(0,0,0,.8)';
        this.curvyness = 0;

        //default is one directional arrow
        this.isTwoWay = false;

        //default is basic link with no points
        this.addSourceMarker = true;
        this.addTargetMarker = true;
    }
}

/**
 * Create link with arrowheads
 */
class ArrowLinkFactory extends DefaultLinkFactory {
    getNewInstance = (initialConfig) => {
        return new ArrowLinkModel();
    };

    generateReactWidget(diagramEngine, link) {
        //use custom link widget
        return React.createElement(ArrowLinkWidget, {
            link,
            diagramEngine
        });
    }

    generateLinkSegment = (model, widget, selected, path) => {
        // add custom arrowheads to link path
        // defined in dependency tree render
        const arrow = 'url(#arrow)';

        let displayMarkerStart = false;
        let displayMarkerEnd = false;

        if (model.addSourceMarker && model.isTwoWay) {
            displayMarkerStart = true;
        }

        if (model.addTargetMarker) {
            displayMarkerEnd = true;
        }

        return (
            <g>
                <path
                    className={selected ? widget.bem('--path-selected') : ''}
                    strokeWidth={model.width}
                    stroke={model.color}
                    d={path}
                    markerStart={displayMarkerStart ? arrow : ''}
                    markerEnd={displayMarkerEnd ? arrow : ''}
                />
            </g>
        );
    };
}

/**
 * Prevent link path from changing direction when node is moved
 */
class ArrowLinkWidget extends DefaultLinkWidget {
    render = () => {
        // edits to default render method to prevent arrows from changing direction
        // also just cut out stuff that we aren't using personally

        const { diagramEngine } = this.props;
        if (!diagramEngine.nodesRendered) {
            return null;
        }

        //ensure id is present for all points on the path
        const points = this.props.link.points;
        const paths = [];

        if (points.length === 2) {

            const pointLeft = points[1];
            const pointRight = points[0];

            // REMOVED because it changes the direction of the path which is needed for arrowheads to remain
            // flowing the correct direction
            // I'm forcing straight lines anyway

            //some defensive programming to make sure the smoothing is
            //always in the right direction
            /*if (pointLeft[xOrY] > pointRight[xOrY]) {
             pointLeft = points[1];
             pointRight = points[0];
             }*/

            paths.push(
                this.generateLink(
                    Toolkit.generateLinePath(pointLeft, pointRight),
                    {
                        onMouseDown: event => {
                            this.addPointToLink(event, 1);
                        }
                    },
                    '0'
                )
            );

            // draw the link as dangeling
            if (this.props.link.targetPort === null) {
                paths.push(this.generatePoint(1));
            }
        } else {
            //draw the multiple anchors and complex line instead
            for (let j = 0; j < points.length - 1; j++) {
                paths.push(
                    this.generateLink(
                        Toolkit.generateLinePath(points[j + 1], points[j]),
                        {
                            'data-linkid': this.props.link.id,
                            'data-point': j,
                            onMouseDown: (event) => {
                                this.addPointToLink(event, j + 1);
                            }
                        },
                        j
                    )
                );
            }

            //render the circles
            for (let i = 1; i < points.length - 1; i++) {
                paths.push(this.generatePoint(i));
            }

            if (this.props.link.targetPort === null) {
                paths.push(this.generatePoint(points.length - 1));
            }
        }

        this.refPaths = [];
        return (
            <g {...this.getProps()}>
                {paths}
                {_.map(this.props.link.labels, labelModel => {
                    return this.generateLabel(labelModel);
                })}
            </g>
        );
    }
}

/**
 * Port to use custom link model and always be locked
 */
class CustomPortModel extends DefaultPortModel {
    createLinkModel = () => {
        return new ArrowLinkModel();
    };

    isLocked = () => {
        //lock ports so user can't create new links
        return true;
    };
}

/**
 * Display node label instead of name
 */
class CustomNodeWidget extends DefaultNodeWidget {
    navigateToDependencies = () => {
        const segs = this.props.node.name.replace('-raw', '').split(':');
        const id = segs[0];
        NavigationUtils.gotoDependencyTree(id, false);
    };

    render = () => {
        let icon = null;
        if (this.props.node.icon) {
            icon = <i className={`fa ${this.props.node.icon}`} />;
        }

        const props = { ...this.getProps() };
        if (this.props.node.isSource) {
            props.className = `${props.className} source-node`;
        }

        return (
            <div {...props} style={{ background: this.props.node.color }} onDoubleClick={this.navigateToDependencies}>
                <div className={this.bem('__title')}>
                    <div className={this.bem('__name')}>{icon}{this.props.node.label}</div>
                </div>
                <div className={this.bem('__ports')}>
                    <div className={this.bem('__in')}>
                        {_.map(this.props.node.getInPorts(), this.generatePort.bind(this))}
                    </div>
                    <div className={this.bem('__out')}>
                        {_.map(this.props.node.getOutPorts(), this.generatePort.bind(this))}
                    </div>
                </div>
            </div>
        );
    };
}

/**
 * Use custom node widget
 */
class CustomNodeFactory extends DefaultNodeFactory {
    getNewInstance = (initialConfig) => {
        return new CustomNodeModel();
    };

    generateReactWidget = (diagramEngine, node) => {
        return React.createElement(CustomNodeWidget, {
            node,
            diagramEngine
        });
    };
}

/**
 * Set label prop for node model
 */
class CustomNodeModel extends DefaultNodeModel {
    constructor(name, color, label, icon) {
        super(name, color);
        this.label = label;
        this.icon = icon;
    }
}


// WEBPACK FOOTER //
// ./src/components/page/process/DependencyTree.js